import React, { createContext, useContext, useEffect, useState } from "react";
import { StoreContext } from "../../business/Provider";
import { Toast } from "../../components/toast";
import useReload from "../../hooks/useReload";
import { useIncidences } from "../../hooks/incidences/useIncidences";
import { FilterMatchMode } from "primereact/api";
import { usePeriods } from "../../hooks/periods/usePerdiods";
import { GeneralNames } from "../../namesConstants/names";
import {
  GET_INCIDENCES_BY_PROCESS_ID_ClEAR,
  LOADING_OFF,
  LOADING_ON,
} from "../../business/constants";
import {
  downloadFileBase64,
  downloadFileExcel,
  formatDate,
  getFileNameWithDateTime,
} from "../../utils/utils";
import { tableColumnsData } from "../../screens/Incidenses/SendIncidences/utils";
import moment from "moment";
import { useExport } from "../../hooks/export/useExport";

import {
  appName,
  formatDateForExcelHeader,
  formatDateWithoutTime,
  sortArray,
} from "../../utils/excelUtils";
import { SendIncidencesFilterContext } from "../../context/wrapperContext/SendIncidences/SendIncidencesFilterContext";

export const SendIncidencesServiceContext = createContext();

const DEFAULT_SORT = [
  {
    field: "incidenceDate",
    order: -1,
  },
];

export const SendIncidencesServiceManagerProvider = ({ children }) => {
  const filterContext = useContext(SendIncidencesFilterContext);

  const { exportPayRollData } = useExport();

  const [toSend, setToSend] = useState([]);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [incidences, setIncidences] = useState([]);
  const [statusBar, setStatusBar] = useState(0);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [showDialog, setShowDialog] = useState(false);

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    statusText: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const [valueInputFilters, setValueInputFilters] = useState({
    statusText: "",
  });

  const isPayrollCall = JSON.parse(sessionStorage.getItem("isPayrollCall"));

  const {
    getPeriodState,
    getIncidencesState,
    getIncidencesStateByProcessId,
    dispatchGetIncidencesByProcessId,
    loadingState,
    dispatchLoading,
    companyState,
  } = useContext(StoreContext);

  const { getIncidences, postIncidences } = useIncidences(
    {
      StartDate: filterContext.period?.initialDate,
      EndDate: filterContext.period?.finalDate,
      PeriodTypeId: filterContext.typePeriod?.periodType,
    },
    setStatusBar
  );

  const { getPeriods } = usePeriods();

  useReload([getDataPeriods, getPeriods]);

  useEffect(() => {
    if (
      Array.isArray(getPeriodState.periods) &&
      getPeriodState.periods.length !== 0 &&
      filterContext.isLoadTypesPeriods === false
    ) {
      const typePeriods = getPeriodState.periods;

      const periodWithPeriods =
        typePeriods.find((item) => item.periods && item.periods.length > 0) ??
        typePeriods[0];

      filterContext.setTypePeriods(typePeriods);
      validatePeriods(periodWithPeriods);
      filterContext.setIsLoadTypesPeriods(true);
    }
  }, [getPeriodState.periods, filterContext.isLoadTypesPeriods]);

  useEffect(() => {
    if (getIncidencesState.incidences !== null) {
      const data = getIncidencesState?.incidences?.results ?? [];

      const newResults = data.map((incidence) => ({
        ...incidence,
        errorDetail: incidence.errorDetail === null ? "" : incidence.errorDetail,
      }));

      setIncidences(newResults);
    }
  }, [getIncidencesState]);

  useEffect(() => {
    if (getIncidencesStateByProcessId?.data !== null) {
      const { data } = getIncidencesStateByProcessId.data;

      setStatusBar(data?.step);

      if (data?.step === 4) {
        setTimeout(async () => {
          setShowDialog(false);
          setStatusBar(null);
        }, 1000);
      }
    }
  }, [getIncidencesStateByProcessId]);

  /**
   * Valida los periodos disponibles para el tipo seleccionado.
   * Usa únicamente los periodos recibidos en period/types.
   */
  function validatePeriods(periodData) {
    if (periodData !== undefined) {
      filterContext.setTypePeriod(periodData);

      const periods = periodData.periods ?? [];

      const periodsConverter = periods.map((item) => ({
        ...item,
        name:
          item.name +
          " - " +
          formatDate(item.initialDate) +
          " al " +
          formatDate(item.finalDate),
      }));

      filterContext.setPeriods(periodsConverter);

      if (periodsConverter.length === 0) {
        filterContext.setPeriod(null);
        setIncidences([]);
        return;
      }

      const period = periodSelectFunc(periodsConverter);

      if (period !== null) {
        filterContext.setPeriod(period);
        return getIncidencesAction(period, periodData?.periodType);
      }

      filterContext.setPeriod(null);
      setIncidences([]);
      return;
    }

    filterContext.setTypePeriod(null);
    filterContext.setPeriods([]);
    filterContext.setPeriod(null);
    setIncidences([]);
  }

  async function getDataPeriods() {
    return await getIncidencesAction(
      filterContext.period,
      filterContext.typePeriod?.periodType
    );
  }

  function periodSelectFunc(periodos) {
    if (!periodos || periodos.length === 0) {
      return null;
    }

    const fechaActual = moment();

    const orderedPeriods = [...periodos].sort((a, b) =>
      moment(a.initialDate).diff(moment(b.initialDate))
    );

    const periodoActual = orderedPeriods.find((periodo) => {
      const fechaInicial = moment(periodo.initialDate);
      const fechaFinal = moment(periodo.finalDate);

      return fechaActual.isBetween(fechaInicial, fechaFinal, undefined, "[]");
    });

    if (periodoActual) {
      return periodoActual;
    }

    const periodAfter = orderedPeriods.find((periodo) =>
      moment(periodo.initialDate).isSameOrAfter(fechaActual)
    );

    if (periodAfter) {
      Toast(
        "",
        "Se está consultando incidencias de un periodo cercano a la fecha actual",
        "",
        ""
      );

      return periodAfter;
    }

    const periodBefore = [...orderedPeriods]
      .reverse()
      .find((periodo) => moment(periodo.finalDate).isSameOrBefore(fechaActual));

    if (periodBefore) {
      Toast("", "Se está consultando incidencias de un periodo anterior", "", "");

      return periodBefore;
    }

    Toast("warning", "No se encontró un periodo cercano a la fecha actual", "", "");

    return null;
  }

  async function handleTypePeriodSelectChange(data) {
    filterContext.setTypePeriod(data);

    const periods = data?.periods ?? [];

    const periodsConverter = periods.map((item) => ({
      ...item,
      name:
        item.name +
        " - " +
        formatDate(item.initialDate) +
        " al " +
        formatDate(item.finalDate),
    }));

    filterContext.setPeriods(periodsConverter);

    if (periodsConverter.length === 0) {
      filterContext.setPeriod(null);
      setIncidences([]);
      return;
    }

    const period = periodSelectFunc(periodsConverter);

    filterContext.setPeriod(period);

    return period
      ? await getIncidencesAction(period, data?.periodType)
      : setIncidences([]);
  }

  async function handlePeriodSelectChange(data) {
    filterContext.setPeriod(data);

    return await getIncidencesAction(
      data,
      filterContext.typePeriod?.periodType
    );
  }

  async function getIncidencesAction(periodSelect, periodType) {
    if (
      !periodSelect ||
      !periodSelect.initialDate ||
      !periodSelect.finalDate ||
      !periodType
    ) {
      setIncidences([]);
      return null;
    }

    const params = {
      StartDate: periodSelect.initialDate,
      EndDate: periodSelect.finalDate,
      PeriodTypeId: periodType,
    };

    return await getIncidences(params);
  }

  function onGlobalFilterChange(e) {
    const { value } = e.target;

    const _filters = { ...filters };
    _filters.global.value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  }

  function clearTextLabel() {
    setGlobalFilterValue("");

    setFilters({
      global: { value: "", matchMode: FilterMatchMode.CONTAINS },
    });
  }

  const bodyDate = (rowData) => {
    const date = moment.utc(rowData);
    date.startOf("day");

    return date.format("DD/MM/YYYY");
  };

  const statusChange = (type) => {
    switch (type) {
      case 1:
        return "005EB8";
      case 2:
        return "FF8585";
      default:
        return "65CD81";
    }
  };

  function dataToExcel() {
    const formatedIncidences = sortArray(
      JSON.parse(JSON.stringify(incidences)),
      sort.length > 0 ? sort[0] : DEFAULT_SORT
    );

    const dataExcel = formatedIncidences.map(
      ({
        code,
        department,
        errorDetail,
        incidenceDate,
        incidenceTime,
        justifiedBy,
        justifiedOn,
        name,
        shiftBegins,
        shiftEnds,
        status,
        statusText,
        typeIncidence,
        workShift,
      }) => ({
        code: {
          value: code,
          style: {},
        },
        errorDetail: {
          value: errorDetail,
          style: { wrapText: true },
        },
        incidenceDate: {
          value: bodyDate(incidenceDate),
          style: {
            horizontalAlignment: "center",
            verticalAlignment: "bottom",
          },
        },
        incidenceTime: {
          value: incidenceTime,
          style: {},
        },
        justifiedBy: {
          value: justifiedBy,
          style: {},
        },
        justifiedOn: {
          value: bodyDate(justifiedOn),
          style: {},
        },
        name: {
          value: name,
          style: {},
        },
        department: {
          value: department,
          style: {},
        },
        shiftBegins: {
          value: shiftBegins,
          style: {},
        },
        shiftEnds: {
          value: shiftEnds,
          style: {},
        },
        statusText: {
          value: statusText,
          style: {
            fontColor: statusChange(status),
            horizontalAlignment: "center",
            verticalAlignment: "bottom",
          },
        },
        typeIncidence: {
          value: typeIncidence,
          style: {},
        },
        workShift: {
          value: workShift,
          style: {},
        },
      })
    );

    const toExcel = {
      filename: "Envio de Incidencias",
      templateFileName: "query_template.xlsx",
      replace: [
        {
          type: 0,
          tag: "#simple.name",
          prop: "name",
          default: "Simple",
        },
        {
          type: 1,
          tag: "#simple.company",
          prop: "company",
          default: "Simple",
        },
        {
          type: 1,
          tag: "#printDate",
          prop: "printDate",
          default: new Date().toLocaleDateString(),
          format: ["numberFormat", "dd mmmm yyyy"],
        },
        {
          type: 1,
          tag: "#rangeDate",
          prop: "rangeDate",
          default: new Date().toLocaleDateString(),
        },
        {
          type: 1,
          tag: "#simple.appName",
          prop: "appName",
          default: "Simple",
        },
        {
          type: 1,
          tag: "#simple.name",
          prop: "report",
          default: "Simple",
        },
        {
          type: 2,
          tag: "#simple.cols",
          prop: "cols",
        },
        {
          type: 4,
          tag: "#simple.rows",
          prop: "rows",
          colprop: "cols",
        },
      ],
      data: {
        company: companyState?.companies?.name
          ? companyState.companies.name
          : "",
        autoFilter: true,
        gridLinesVisible: true,
        printDate: formatDateForExcelHeader(new Date()),
        rangeDate: filterContext.period
          ? `${formatDateWithoutTime(
              filterContext.period.initialDate
            )} - ${formatDateWithoutTime(filterContext.period.finalDate)}`
          : "",
        appName: appName,
        name: "Envio de incidencias",
        report: `Incidencias - ${filterContext.typePeriod?.name ?? ""} - ${
          filterContext.period?.name ?? ""
        }`,
        cols: [],
        rows: dataExcel,
      },
    };

    tableColumnsData.forEach((header, index) => {
      let width = 35;

      switch (tableColumnsData[index].field) {
        case "code":
          width = 12;
          break;
        case "errorDetail":
          width = 50;
          break;
        case "incidenceDate":
          width = 25;
          break;
        case "incidenceTime":
          width = 12;
          break;
        case "statusText":
          width = 20;
          break;
        default:
          break;
      }

      const col = {
        title: tableColumnsData[index].label,
        prop: tableColumnsData[index].field,
        width: width,
      };

      toExcel.data.cols.push(col);
    });

    return toExcel;
  }

  async function toExcelExport() {
    if (!filterContext.period) {
      Toast("warning", "No hay periodo seleccionado");
      return;
    }

    if (incidences.length > 0) {
      convertSortToString();

      dispatchLoading({ type: LOADING_ON });

      const toExcel = dataToExcel();
      const filName = getFileNameWithDateTime("Envío_Incidencias");

      await downloadFileBase64(toExcel, "xlsx", filName)
        .then(() => {
          dispatchLoading({ type: LOADING_OFF });
        })
        .catch(() => {
          dispatchLoading({ type: LOADING_OFF });
        });
    } else {
      Toast(
        "warning",
        GeneralNames.EmptyDataMessageExport,
        GeneralNames.EmptyDataMessageBodyExport,
        "success"
      );
    }
  }

  const convertSortToString = () => {
    const { field, order } = sort.length > 0 ? sort[0] : DEFAULT_SORT[0];
    const direction = order === 1 ? "ASC" : "DESC";

    let fieldName = "";

    if (field === "code") fieldName = "employeeCode";
    else if (field === "name") fieldName = "employeeName";
    else if (field === "department") fieldName = "department";
    else return "";

    return `${fieldName}-${direction}`;
  };

  const iconsData = [
    {
      icon: "excel",
      title: "Exportar a EXCEL",
      onClick: isPayrollCall ? toExcelExportPayroll : toExcelExport,
    },
    ...(sessionStorage.getItem("isPayrollCall") === "false"
      ? [
          {
            icon: "sendEvidences",
            title: "Enviar incidencias a la nómina",
            onClick: sendIncidences,
          },
        ]
      : []),
  ];

  async function toExcelExportPayroll() {
    if (!filterContext.period?.periodId) {
      Toast("warning", "No hay periodo seleccionado");
      return;
    }

    if (incidences.length > 0) {
      try {
        const result = await exportPayRollData({
          periodId: filterContext.period.periodId,
          sort: convertSortToString(),
          search: globalFilterValue,
        });

        if (result.status === 200) {
          dispatchLoading({ type: LOADING_ON });

          const fillName = getFileNameWithDateTime("Envío_Incidencias");

          await downloadFileExcel(result, fillName)
            .then(() => {
              dispatchLoading({ type: LOADING_OFF });
            })
            .catch(() => {
              dispatchLoading({ type: LOADING_OFF });
            });
        }

        return result;
      } catch (error) {
        dispatchLoading({ type: LOADING_OFF });
        Toast("warning", "No se pudo exportar el archivo de incidencias");
      }
    } else {
      Toast(
        "warning",
        GeneralNames.EmptyDataMessageExport,
        GeneralNames.EmptyDataMessageBodyExport,
        "success"
      );
    }
  }

  async function sendIncidences() {
    if (
      !filterContext.period?.initialDate ||
      !filterContext.period?.finalDate ||
      !filterContext.typePeriod?.periodType
    ) {
      Toast("warning", "No hay periodo seleccionado");
      return;
    }

    if (incidences.length > 0 && toSend.length > 0) {
      dispatchGetIncidencesByProcessId({
        type: GET_INCIDENCES_BY_PROCESS_ID_ClEAR,
      });

      setShowDialog(true);

      const idsIncidences = toSend.map((incidence) => incidence.attendanceId);

      const filters = {
        StartDate: filterContext.period.initialDate,
        EndDate: filterContext.period.finalDate,
        PeriodTypeId: filterContext.typePeriod.periodType,
      };

      try {
        return await postIncidences(idsIncidences, filters);
      } finally {
        setToSend([]);
      }
    } else {
      Toast("warning", "No hay incidencias por enviar");
    }
  }

  return (
    <SendIncidencesServiceContext.Provider
      value={{
        typePeriod: filterContext.typePeriod,
        typePeriods: filterContext.typePeriods,
        period: filterContext.period,
        periods: filterContext.periods,
        handlePeriodSelectChange,
        handleTypePeriodSelectChange,
        clearTextLabel,
        onGlobalFilterChange,
        globalFilterValue,
        iconsData,
        loadingState,
        incidences,
        filters,
        setFilters,
        toSend,
        setToSend,
        sort,
        setSort,
        valueInputFilters,
        setValueInputFilters,
        showDialog,
        statusBar,
      }}
    >
      {children}
    </SendIncidencesServiceContext.Provider>
  );
};